import {Injectable, Injector, Renderer2} from '@angular/core';
import {BehaviorSubject, Observable, Observer, Subscription, TeardownLogic} from 'rxjs';
import {pairwise, startWith} from 'rxjs/operators';
import {DynamicComponentFactoryService, DynamicComponentRef} from '../dynamic-component';
import {FloatMarkupDirective} from '../float-markup';
import {OverlayComponent} from './overlay.component';

interface OverlayOptions {
    target: DynamicComponentRef<any>;
    elementsAboveOverlay$?: BehaviorSubject<FloatMarkupDirective[]>;
    destroyWhen?: Observable<any>;
    onOverlayCreated?: (overlayRef: DynamicComponentRef<OverlayComponent>) => void;
}

@Injectable()
export class OverlayService {

    constructor(
        private dynamicComponentFactoryService: DynamicComponentFactoryService,
        private injector: Injector
    ) {}

    public create(options: OverlayOptions): Observable<MouseEvent> {
        return new Observable((observer: Observer<MouseEvent>): TeardownLogic => {
            const subscription = new Subscription();
            const overlay = this.dynamicComponentFactoryService.create(OverlayComponent, {
                injector: this.injector,
                place: {
                    before: options.target
                },
                onDestroy: () => observer.complete(),
                destroyWhen: options.destroyWhen || options.target.destroy$,
                configure: overlayComponent => {
                    overlayComponent.inheritViewEncapsulation(
                        options.target.componentRef.injector.get(Renderer2)
                    );

                    subscription.add(overlayComponent.click$.subscribe(event => observer.next(event)));
                }
            });

            if (options.elementsAboveOverlay$) {
                subscription.add(
                    this.listenToElementsAboveOverlay(overlay, options.elementsAboveOverlay$)
                );
            }

            if (options.onOverlayCreated) {
                options.onOverlayCreated(overlay);
            }

            return () => {
                subscription.unsubscribe();
                overlay.destroy();
            };
        });
    }

    private listenToElementsAboveOverlay(
        overlay: DynamicComponentRef<OverlayComponent>,
        elementsAboveOverlay$: BehaviorSubject<FloatMarkupDirective[]>
    ): Subscription {
        const subscription = elementsAboveOverlay$.pipe(
            startWith([]),
            pairwise()
        ).subscribe(([prevElements, elements]: [FloatMarkupDirective[], FloatMarkupDirective[]]) => {
            prevElements.forEach(element => element.reset());
            elements.forEach(element => element.float(overlay));
        });

        subscription.add(() => elementsAboveOverlay$.value.forEach(element => element.reset()));

        return subscription;
    }

}
